import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { useOnboardingFlow } from "../OnboardingFlowContext";
import { getOnboardingMessages } from "../onboardingI18n";
import { getReservationSettingsMessages } from "../reservationSettingsMessages";
import "../OnboardingApp.css";

const RESERVATION_ROLE_ORDER = ["booker", "main_guest", "accompanying", "child"];

function createEmptyRequiredFieldsMap() {
  return RESERVATION_ROLE_ORDER.reduce((accumulator, roleKey) => {
    accumulator[roleKey] = [];
    return accumulator;
  }, {});
}

function normalizeExternalReservationNotificationEmails(value) {
  if (Array.isArray(value)) {
    const seen = new Set();

    return value
      .flatMap((item) => {
        if (typeof item === "string") return [item];
        if (item && typeof item === "object") {
          return [item.email, item.value, item.address].filter(Boolean);
        }
        return [];
      })
      .map((item) => String(item || "").trim())
      .filter((item) => {
        const normalizedKey = item.toLowerCase();
        if (!normalizedKey || seen.has(normalizedKey)) return false;
        seen.add(normalizedKey);
        return true;
      });
  }

  const raw = String(value || "").trim();
  if (!raw) return [];

  const seen = new Set();
  return raw
    .split(/[,;\n]+/g)
    .map((item) => String(item || "").trim())
    .filter((item) => {
      const normalizedKey = item.toLowerCase();
      if (!normalizedKey || seen.has(normalizedKey)) return false;
      seen.add(normalizedKey);
      return true;
    });
}

function mergeExternalReservationNotificationEmailList(currentValue, incomingValue) {
  return normalizeExternalReservationNotificationEmails([
    ...normalizeExternalReservationNotificationEmails(currentValue),
    ...normalizeExternalReservationNotificationEmails(incomingValue),
  ]);
}

function createEmptyDraft() {
  return {
    checkin_time: "14:00",
    checkout_time: "10:00",
    max_age_baby: 2,
    max_age_child: 12,
    min_age_main_guest: 18,
    min_age_booker: 18,
    required_customer_fields_reservation: createEmptyRequiredFieldsMap(),
    required_customer_roles_reservation: ["booker"],
    group_reservations_default_status: "Confirmed",
    reservation_confirmation_email_templates: {
      en: { subject: "", body: "" },
      es: { subject: "", body: "" },
    },
    reservation_confirmation_email_languages: ["en", "es"],
    external_reservation_notifications_enabled: false,
    external_reservation_notification_emails: [],
  };
}

function normalizeIncomingDraft(rawDraft) {
  const nextDraft = createEmptyDraft();
  const source = rawDraft && typeof rawDraft === "object" ? rawDraft : {};

  const normalizeTime = (value, fallbackValue) => {
    const rawValue = String(value || fallbackValue || "").trim();
    const match = rawValue.match(/^(\d{2}:\d{2})(?::\d{2})?$/);
    return match ? match[1] : fallbackValue;
  };

  const normalizeInteger = (value, fallbackValue) => {
    if (value === "" || value === null || typeof value === "undefined") {
      return fallbackValue;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallbackValue;
  };

  nextDraft.checkin_time = normalizeTime(source.checkin_time, nextDraft.checkin_time);
  nextDraft.checkout_time = normalizeTime(source.checkout_time, nextDraft.checkout_time);
  nextDraft.max_age_baby = normalizeInteger(source.max_age_baby, nextDraft.max_age_baby);
  nextDraft.max_age_child = normalizeInteger(source.max_age_child, nextDraft.max_age_child);
  nextDraft.min_age_main_guest = normalizeInteger(
    source.min_age_main_guest,
    nextDraft.min_age_main_guest
  );
  nextDraft.min_age_booker = normalizeInteger(source.min_age_booker, nextDraft.min_age_booker);

  const incomingFields =
    source.required_customer_fields_reservation &&
    typeof source.required_customer_fields_reservation === "object" &&
    !Array.isArray(source.required_customer_fields_reservation)
      ? source.required_customer_fields_reservation
      : {};

  nextDraft.required_customer_fields_reservation = RESERVATION_ROLE_ORDER.reduce(
    (accumulator, roleKey) => {
      accumulator[roleKey] = Array.isArray(incomingFields?.[roleKey])
        ? incomingFields[roleKey].map((value) => String(value || "").trim()).filter(Boolean)
        : [];
      return accumulator;
    },
    {}
  );

  nextDraft.required_customer_roles_reservation = Array.isArray(
    source.required_customer_roles_reservation
  )
    ? source.required_customer_roles_reservation.map((value) => String(value || "").trim()).filter(Boolean)
    : ["booker"];
  nextDraft.group_reservations_default_status =
    String(source.group_reservations_default_status || "Confirmed").trim() || "Confirmed";

  const templates =
    source.reservation_confirmation_email_templates &&
    typeof source.reservation_confirmation_email_templates === "object" &&
    !Array.isArray(source.reservation_confirmation_email_templates)
      ? source.reservation_confirmation_email_templates
      : {};

  nextDraft.reservation_confirmation_email_templates = {
    en: {
      subject: String(templates?.en?.subject || "").trim(),
      body: String(templates?.en?.body || "").trim(),
    },
    es: {
      subject: String(templates?.es?.subject || "").trim(),
      body: String(templates?.es?.body || "").trim(),
    },
  };

  nextDraft.reservation_confirmation_email_languages = Array.isArray(
    source.reservation_confirmation_email_languages
  )
    ? source.reservation_confirmation_email_languages
        .map((value) => String(value || "").trim().toLowerCase())
        .filter((value, index, array) => value && array.indexOf(value) === index)
    : ["en", "es"];

  if (!nextDraft.reservation_confirmation_email_languages.length) {
    nextDraft.reservation_confirmation_email_languages = ["en", "es"];
  }

  nextDraft.external_reservation_notifications_enabled =
    source.external_reservation_notifications_enabled === true;
  nextDraft.external_reservation_notification_emails =
    normalizeExternalReservationNotificationEmails(
      source.external_reservation_notification_emails
    );

  return nextDraft;
}

function buildPayload(draft) {
  return {
    checkin_time: draft.checkin_time,
    checkout_time: draft.checkout_time,
    max_age_baby: draft.max_age_baby,
    max_age_child: draft.max_age_child,
    min_age_main_guest: draft.min_age_main_guest,
    min_age_booker: draft.min_age_booker,
    required_customer_fields_reservation: draft.required_customer_fields_reservation,
    reservation_confirmation_email_languages: draft.reservation_confirmation_email_languages,
    external_reservation_notifications_enabled:
      draft.external_reservation_notifications_enabled,
    external_reservation_notification_emails:
      normalizeExternalReservationNotificationEmails(
        draft.external_reservation_notification_emails
      ),
  };
}

export default function ReservationSettingsStep() {
  const { languageId } = usePmsTheme();
  const { clearStepCommandState, markStepComplete, markStepIncomplete, setStepCommandState, steps } =
    useOnboardingFlow();
  const onboardingCopy = getOnboardingMessages(languageId);
  const copy = getReservationSettingsMessages(languageId);
  const [draft, setDraft] = useState(() => createEmptyDraft());
  const [sessionMeta, setSessionMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [externalReservationNotificationEmailInput, setExternalReservationNotificationEmailInput] =
    useState("");
  const feedbackTimerRef = useRef(null);
  const stepNumber = useMemo(
    () => Math.max(steps.findIndex((step) => step.id === "reservation-settings") + 1, 1),
    [steps]
  );

  const totalSelectedFields = useMemo(
    () =>
      Object.values(draft.required_customer_fields_reservation || {}).reduce(
        (total, roleFields) => total + (Array.isArray(roleFields) ? roleFields.length : 0),
        0
      ),
    [draft.required_customer_fields_reservation]
  );

  const selectedConfirmationLanguageLabels = useMemo(
    () =>
      (draft.reservation_confirmation_email_languages || []).map(
        (languageKey) => copy.confirmationLanguages[languageKey] || languageKey.toUpperCase()
      ),
    [copy.confirmationLanguages, draft.reservation_confirmation_email_languages]
  );

  const externalReservationNotificationRecipients = useMemo(
    () =>
      normalizeExternalReservationNotificationEmails(
        draft.external_reservation_notification_emails
      ),
    [draft.external_reservation_notification_emails]
  );

  const formattedSavedAt = useMemo(() => {
    if (!sessionMeta?.updated_at) return copy.summaryNotSaved;
    const parsedDate = new Date(sessionMeta.updated_at);
    if (Number.isNaN(parsedDate.getTime())) return copy.summaryNotSaved;
    return new Intl.DateTimeFormat(languageId || "en", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(parsedDate);
  }, [copy.summaryNotSaved, languageId, sessionMeta?.updated_at]);

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setFeedback({ type: "", message: "" });

    try {
      const response = await axios.get("/api/onboarding/reservation-settings");
      setDraft(normalizeIncomingDraft(response.data?.draft));
      setSessionMeta(response.data?.session || null);
      setIsDirty(false);
      setLoadFailed(false);
      if (response.data?.session) markStepComplete("reservation-settings");
      else markStepIncomplete("reservation-settings");
    } catch (error) {
      console.error("[Onboarding][ReservationSettings] Error loading draft:", error);
      setDraft(createEmptyDraft());
      setSessionMeta(null);
      setLoadFailed(true);
      markStepIncomplete("reservation-settings");
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.loadError });
    } finally {
      setLoading(false);
    }
  }, [copy.loadError, markStepComplete, markStepIncomplete]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  useEffect(() => {
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }

    if (!feedback.message || loadFailed) {
      return undefined;
    }

    feedbackTimerRef.current = setTimeout(() => {
      setFeedback((currentFeedback) =>
        currentFeedback.message === feedback.message && currentFeedback.type === feedback.type
          ? { type: "", message: "" }
          : currentFeedback
      );
      feedbackTimerRef.current = null;
    }, feedback.type === "success" ? 2400 : 3200);

    return () => {
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }
    };
  }, [feedback, loadFailed]);

  const updateDraft = (updater) => {
    setDraft((currentDraft) => {
      const nextDraft =
        typeof updater === "function" ? updater(currentDraft) : { ...currentDraft, ...updater };
      return nextDraft;
    });
    setIsDirty(true);
    setFeedback({ type: "", message: "" });
  };

  const toggleFieldForRole = (roleKey, fieldKey) => {
    updateDraft((currentDraft) => {
      const currentFields = Array.isArray(currentDraft.required_customer_fields_reservation?.[roleKey])
        ? currentDraft.required_customer_fields_reservation[roleKey]
        : [];
      const nextFields = currentFields.includes(fieldKey)
        ? currentFields.filter((value) => value !== fieldKey)
        : [...currentFields, fieldKey];

      return {
        ...currentDraft,
        required_customer_fields_reservation: {
          ...currentDraft.required_customer_fields_reservation,
          [roleKey]: nextFields,
        },
      };
    });
  };

  const toggleConfirmationLanguage = (languageKey) => {
    updateDraft((currentDraft) => {
      const currentLanguages = Array.isArray(currentDraft.reservation_confirmation_email_languages)
        ? currentDraft.reservation_confirmation_email_languages
        : [];

      const nextLanguages = currentLanguages.includes(languageKey)
        ? currentLanguages.filter((value) => value !== languageKey)
        : [...currentLanguages, languageKey];

      return {
        ...currentDraft,
        reservation_confirmation_email_languages: nextLanguages,
      };
    });
  };

  const addExternalReservationNotificationRecipients = (rawValue) => {
    updateDraft((currentDraft) => ({
      ...currentDraft,
      external_reservation_notification_emails: mergeExternalReservationNotificationEmailList(
        currentDraft.external_reservation_notification_emails,
        rawValue
      ),
    }));
  };

  const commitExternalReservationNotificationRecipientInput = () => {
    const rawValue = String(externalReservationNotificationEmailInput || "");
    if (!rawValue.trim()) return;
    addExternalReservationNotificationRecipients(rawValue);
    setExternalReservationNotificationEmailInput("");
  };

  const removeExternalReservationNotificationRecipient = (indexToRemove) => {
    updateDraft((currentDraft) => ({
      ...currentDraft,
      external_reservation_notification_emails: normalizeExternalReservationNotificationEmails(
        currentDraft.external_reservation_notification_emails
      ).filter((_, index) => index !== indexToRemove),
    }));
  };

  const validateBeforeSave = useCallback((draftToValidate) => {
    if (
      !String(draftToValidate.checkin_time || "").trim() ||
      !String(draftToValidate.checkout_time || "").trim()
    ) {
      return copy.validationTimes;
    }

    if (Number(draftToValidate.max_age_baby) > Number(draftToValidate.max_age_child)) {
      return copy.validationAgeRange;
    }

    if (!draftToValidate.reservation_confirmation_email_languages?.length) {
      return copy.validationConfirmationLanguages;
    }

    if (
      draftToValidate.external_reservation_notifications_enabled &&
      !normalizeExternalReservationNotificationEmails(
        draftToValidate.external_reservation_notification_emails
      ).length
    ) {
      return copy.validationExternalNotificationRecipients;
    }

    return null;
  }, [
    copy.validationAgeRange,
    copy.validationConfirmationLanguages,
    copy.validationExternalNotificationRecipients,
    copy.validationTimes,
  ]);

  const handleSave = useCallback(async () => {
    const pendingRecipients = mergeExternalReservationNotificationEmailList(
      draft.external_reservation_notification_emails,
      externalReservationNotificationEmailInput
    );
    const draftToSave = String(externalReservationNotificationEmailInput || "").trim()
      ? {
          ...draft,
          external_reservation_notification_emails: pendingRecipients,
        }
      : draft;

    const validationError = validateBeforeSave(draftToSave);
    if (validationError) {
      setFeedback({ type: "error", message: validationError });
      return false;
    }

    setSaving(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await axios.put("/api/onboarding/reservation-settings", {
        draft: buildPayload(draftToSave),
      });
      setDraft(normalizeIncomingDraft(response.data?.draft));
      setSessionMeta(response.data?.session || null);
      setExternalReservationNotificationEmailInput("");
      setIsDirty(false);
      markStepComplete("reservation-settings");
      setFeedback({ type: "success", message: copy.saveSuccess });
      return true;
    } catch (error) {
      console.error("[Onboarding][ReservationSettings] Error saving draft:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.saveError });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    copy.saveError,
    copy.saveSuccess,
    draft,
    externalReservationNotificationEmailInput,
    markStepComplete,
    validateBeforeSave,
  ]);

  useEffect(() => {
    setStepCommandState("reservation-settings", {
      onSave: handleSave,
      canSave: !loading && !loadFailed,
      canProceed: !isDirty && !loadFailed,
      isDirty,
      isSaving: saving,
    });
    return () => clearStepCommandState("reservation-settings");
  }, [
    clearStepCommandState,
    handleSave,
    isDirty,
    loadFailed,
    loading,
    saving,
    setStepCommandState,
  ]);

  if (loading) {
    return (
      <div className="onboarding-emptyState">
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{copy.copy}</p>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="onboarding-emptyState onboarding-formCard--centered">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{feedback.message || copy.loadError}</p>
      </div>
    );
  }

  return (
    <div className="onboarding-grid">
      {feedback.message && !loadFailed ? (
        <div className="onboarding-toastOverlay" aria-live="polite">
          <div
            className={`onboarding-toast ${
              feedback.type === "success" ? "onboarding-toast--success" : "onboarding-toast--error"
            }`}
            role={feedback.type === "success" ? "status" : "alert"}
          >
            {feedback.message}
          </div>
        </div>
      ) : null}

      <section className="onboarding-formCard">
        <div className="onboarding-formHeader">
          <div>
            <div className="onboarding-eyebrow">{copy.eyebrow}</div>
            <div className="onboarding-stepCount">
              {onboardingCopy.progress.stepCounter(stepNumber, steps.length)}
            </div>
            <h3 className="onboarding-sectionTitle">{copy.title}</h3>
            <p className="onboarding-sectionText">{copy.copy}</p>
          </div>
        </div>

        <div className="onboarding-settingsStack">
          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.stayTitle}</h4>
                <p>{copy.stayCopy}</p>
              </div>
            </div>
            <div className="onboarding-fields">
              <label className="onboarding-field">
                <span className="onboarding-label">{copy.checkinTime}</span>
                <input
                  className="onboarding-input"
                  type="time"
                  value={draft.checkin_time}
                  onChange={(event) => updateDraft({ checkin_time: event.target.value })}
                />
              </label>
              <label className="onboarding-field">
                <span className="onboarding-label">{copy.checkoutTime}</span>
                <input
                  className="onboarding-input"
                  type="time"
                  value={draft.checkout_time}
                  onChange={(event) => updateDraft({ checkout_time: event.target.value })}
                />
              </label>
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.agesTitle}</h4>
                <p>{copy.agesCopy}</p>
              </div>
            </div>
            <div className="onboarding-fields onboarding-fields--fourCols">
              <label className="onboarding-field">
                <span className="onboarding-label">{copy.maxAgeBaby}</span>
                <input
                  className="onboarding-input"
                  type="number"
                  min="0"
                  step="1"
                  value={draft.max_age_baby}
                  onChange={(event) => updateDraft({ max_age_baby: event.target.value })}
                />
              </label>
              <label className="onboarding-field">
                <span className="onboarding-label">{copy.maxAgeChild}</span>
                <input
                  className="onboarding-input"
                  type="number"
                  min="0"
                  step="1"
                  value={draft.max_age_child}
                  onChange={(event) => updateDraft({ max_age_child: event.target.value })}
                />
              </label>
              <label className="onboarding-field">
                <span className="onboarding-label">{copy.minAgeMainGuest}</span>
                <input
                  className="onboarding-input"
                  type="number"
                  min="0"
                  step="1"
                  value={draft.min_age_main_guest}
                  onChange={(event) => updateDraft({ min_age_main_guest: event.target.value })}
                />
              </label>
              <label className="onboarding-field">
                <span className="onboarding-label">{copy.minAgeBooker}</span>
                <input
                  className="onboarding-input"
                  type="number"
                  min="0"
                  step="1"
                  value={draft.min_age_booker}
                  onChange={(event) => updateDraft({ min_age_booker: event.target.value })}
                />
              </label>
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.requiredFieldsTitle}</h4>
                <p>{copy.requiredFieldsCopy}</p>
              </div>
            </div>
            <div className="onboarding-settingsNotice">{copy.requiredFieldsHint}</div>
            <div className="onboarding-settingsRoleGrid">
              {RESERVATION_ROLE_ORDER.map((roleKey) => {
                const roleCopy = copy.roles[roleKey];
                const selectedFields = draft.required_customer_fields_reservation?.[roleKey] || [];
                return (
                  <article key={roleKey} className="onboarding-settingsRoleCard">
                    <div className="onboarding-settingsRoleHead">
                      <div>
                        <h5>{roleCopy.label}</h5>
                        <p>{roleCopy.description}</p>
                      </div>
                      <span className="onboarding-settingsRoleBadge">
                        {copy.fieldsSelected(selectedFields.length)}
                      </span>
                    </div>
                    <div className="onboarding-settingsPills">
                      {copy.customerFields.map((fieldOption) => {
                        const isActive = selectedFields.includes(fieldOption.value);
                        return (
                          <button
                            key={`${roleKey}-${fieldOption.value}`}
                            type="button"
                            className={`onboarding-settingsPill ${isActive ? "is-active" : ""}`}
                            onClick={() => toggleFieldForRole(roleKey, fieldOption.value)}
                          >
                            {fieldOption.label}
                          </button>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="onboarding-settingsSection onboarding-settingsSection--locked">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.requiredRolesTitle}</h4>
                <p>{copy.requiredRolesCopy}</p>
              </div>
              <span className="onboarding-settingsTag">{copy.lockedTag}</span>
            </div>
            <div className="onboarding-settingsNotice">{copy.requiredRolesHint}</div>
            <div className="onboarding-settingsRoleGrid onboarding-settingsRoleGrid--compact">
              {RESERVATION_ROLE_ORDER.map((roleKey) => {
                const roleCopy = copy.roles[roleKey];
                const isActive = draft.required_customer_roles_reservation.includes(roleKey);
                return (
                  <article
                    key={`locked-role-${roleKey}`}
                    className={`onboarding-settingsRoleCard onboarding-settingsRoleCard--readonly ${
                      isActive ? "is-active" : ""
                    }`}
                  >
                    <div className="onboarding-settingsRoleHead">
                      <div>
                        <h5>{roleCopy.label}</h5>
                        <p>{roleCopy.description}</p>
                      </div>
                      {isActive ? (
                        <span className="onboarding-settingsRoleBadge onboarding-settingsRoleBadge--highlight">
                          {copy.recommendedTag}
                        </span>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="onboarding-settingsSection onboarding-settingsSection--locked">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.reservationFlowTitle}</h4>
                <p>{copy.reservationFlowCopy}</p>
              </div>
              <span className="onboarding-settingsTag">{copy.lockedTag}</span>
            </div>
            <div className="onboarding-settingsNotice">{copy.reservationFlowHint}</div>
            <div className="onboarding-settingsChoiceGrid">
              <article className="onboarding-settingsChoiceCard onboarding-settingsChoiceCard--readonly">
                <div className="onboarding-settingsChoiceHead">
                  <h5>{copy.requestedTitle}</h5>
                </div>
                <p>{copy.requestedCopy}</p>
              </article>
              <article className="onboarding-settingsChoiceCard onboarding-settingsChoiceCard--readonly is-active">
                <div className="onboarding-settingsChoiceHead">
                  <h5>{copy.confirmedTitle}</h5>
                  <span className="onboarding-settingsRoleBadge onboarding-settingsRoleBadge--highlight">
                    {copy.recommendedTag}
                  </span>
                </div>
                <p>{copy.confirmedCopy}</p>
              </article>
            </div>
          </section>

          <section className="onboarding-settingsSection onboarding-settingsSection--locked">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.confirmationTitle}</h4>
                <p>{copy.confirmationCopy}</p>
              </div>
              <span className="onboarding-settingsTag">{copy.lockedTag}</span>
            </div>
            <div className="onboarding-settingsNotice">{copy.confirmationHint}</div>
            <div className="onboarding-field">
              <span className="onboarding-label">{copy.confirmationLanguagesLabel}</span>
              <div className="onboarding-settingsPills">
                {Object.keys(copy.confirmationLanguages).map((languageKey) => {
                  const isActive = draft.reservation_confirmation_email_languages.includes(languageKey);
                  return (
                    <button
                      key={`confirmation-language-${languageKey}`}
                      type="button"
                      className={`onboarding-settingsPill ${isActive ? "is-active" : ""}`}
                      onClick={() => toggleConfirmationLanguage(languageKey)}
                    >
                      {copy.confirmationLanguages[languageKey]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="onboarding-settingsEmailGrid">
              {draft.reservation_confirmation_email_languages.map((languageKey) => {
                const template = draft.reservation_confirmation_email_templates?.[languageKey] || {
                  subject: "",
                  body: "",
                };
                return (
                  <article key={`template-${languageKey}`} className="onboarding-settingsEmailCard">
                    <div className="onboarding-settingsEmailHead">
                      <h5>{copy.confirmationLanguages[languageKey] || languageKey.toUpperCase()}</h5>
                    </div>
                    <div className="onboarding-field">
                      <span className="onboarding-label">{copy.confirmationSubject}</span>
                      <input
                        className="onboarding-input onboarding-settingsReadonlyControl"
                        value={template.subject}
                        readOnly
                      />
                    </div>
                    <div className="onboarding-field">
                      <span className="onboarding-label">{copy.confirmationBody}</span>
                      <textarea
                        className="onboarding-textarea onboarding-settingsReadonlyControl"
                        value={template.body}
                        readOnly
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.externalNotificationTitle}</h4>
                <p>{copy.externalNotificationCopy}</p>
              </div>
              <span className="onboarding-settingsTag">
                {draft.external_reservation_notifications_enabled
                  ? copy.externalNotificationEnabledTag
                  : copy.externalNotificationDisabledTag}
              </span>
            </div>
            <div className="onboarding-settingsNotice">{copy.externalNotificationHint}</div>

            <label className="onboarding-toggleCard">
              <input
                type="checkbox"
                checked={!!draft.external_reservation_notifications_enabled}
                onChange={(event) =>
                  updateDraft({
                    external_reservation_notifications_enabled: event.target.checked,
                  })
                }
              />
              <div className="onboarding-toggleCardBody">
                <span className="onboarding-label">{copy.externalNotificationToggleLabel}</span>
                <span className="onboarding-toggleCardCopy">
                  {copy.externalNotificationToggleCopy}
                </span>
              </div>
            </label>

            <div className="onboarding-field">
              <span className="onboarding-label">{copy.externalNotificationRecipientsLabel}</span>
              <div className="onboarding-recipientList">
                {externalReservationNotificationRecipients.length ? (
                  externalReservationNotificationRecipients.map((email, index) => (
                    <button
                      key={`${email}_${index}`}
                      type="button"
                      className="onboarding-recipientChip"
                      onClick={() => removeExternalReservationNotificationRecipient(index)}
                      title={`${copy.externalNotificationRemoveAction} ${email}`}
                      aria-label={`${copy.externalNotificationRemoveAction} ${email}`}
                    >
                      <span>{email}</span>
                      <span aria-hidden="true">x</span>
                    </button>
                  ))
                ) : (
                  <div className="onboarding-recipientEmpty">
                    {copy.externalNotificationRecipientEmpty}
                  </div>
                )}
              </div>
              <div className="onboarding-recipientInputRow">
                <input
                  className="onboarding-input"
                  type="text"
                  placeholder={copy.externalNotificationRecipientPlaceholder}
                  value={externalReservationNotificationEmailInput}
                  onChange={(event) =>
                    setExternalReservationNotificationEmailInput(event.target.value)
                  }
                  onKeyDown={(event) => {
                    if (
                      event.key !== "Enter" &&
                      event.key !== "," &&
                      event.key !== ";"
                    ) {
                      return;
                    }

                    event.preventDefault();
                    commitExternalReservationNotificationRecipientInput();
                  }}
                  onPaste={(event) => {
                    const pastedText = event.clipboardData?.getData("text") || "";
                    if (!/[,;\n]/.test(pastedText)) return;
                    event.preventDefault();
                    addExternalReservationNotificationRecipients(pastedText);
                    setExternalReservationNotificationEmailInput("");
                  }}
                />
                <button
                  type="button"
                  className="onboarding-secondaryButton"
                  onClick={commitExternalReservationNotificationRecipientInput}
                >
                  {copy.externalNotificationAddAction}
                </button>
              </div>
              <p className="onboarding-inlineHint">{copy.externalNotificationRecipientsHint}</p>
            </div>
          </section>
        </div>
      </section>

      <aside className="onboarding-infoCard">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.summaryTitle}</h3>
        <p className="onboarding-sectionText">{copy.summaryIntro}</p>
        <div className="onboarding-pointsList">
          {copy.summaryPoints.map((point) => (
            <div key={point} className="onboarding-pointItem">
              <span className="onboarding-pointDot" />
              <span>{point}</span>
            </div>
          ))}
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryCheckinLabel}</span>
          <span className="onboarding-kpiValue">{draft.checkin_time}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryCheckoutLabel}</span>
          <span className="onboarding-kpiValue">{draft.checkout_time}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryAgeLabel}</span>
          <span className="onboarding-kpiValue">
            {`${copy.maxAgeBaby}: ${draft.max_age_baby} / ${copy.maxAgeChild}: ${draft.max_age_child}`}
          </span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryFieldsLabel}</span>
          <span className="onboarding-kpiValue">{totalSelectedFields}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryRolesLabel}</span>
          <span className="onboarding-kpiValue">
            {draft.required_customer_roles_reservation
              .map((roleKey) => copy.roles[roleKey]?.label || roleKey)
              .join(", ")}
          </span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryFlowLabel}</span>
          <span className="onboarding-kpiValue">{draft.group_reservations_default_status}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryEmailLabel}</span>
          <span className="onboarding-kpiValue">
            {selectedConfirmationLanguageLabels.length
              ? selectedConfirmationLanguageLabels.join(", ")
              : copy.summaryEmailValue}
          </span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryNotificationLabel}</span>
          <span className="onboarding-kpiValue">
            {draft.external_reservation_notifications_enabled
              ? copy.summaryNotificationEnabled(externalReservationNotificationRecipients.length)
              : copy.summaryNotificationDisabled}
          </span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summarySavedLabel}</span>
          <span className="onboarding-kpiValue">{formattedSavedAt}</span>
        </div>
      </aside>
    </div>
  );
}
